/**
 * NovaMovimentacaoPage Component
 * 
 * Página de criação de nova movimentação de estoque.
 * Permite criar movimentações com múltiplos produtos.
 * 
 * Funcionalidades:
 * - Primeiro card: Código (auto), Tipo, Motivo
 * - Segundo card: Lista de produtos com quantidade
 * - Busca de produtos
 * - Botão flutuante para salvar
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, X, Search, Package, Save } from 'lucide-react'
import FormField from '../../../../components/ERP/Form/FormField'
import FloatingActionButton from '../../../../components/ERP/FloatingActionButton/FloatingActionButton'
import { stockMovementService, stockMovementTypeService, productService } from '../../../../services/erpApi'
import { productCacheHelpers } from '../../../../utils/productCacheEvents'
import './NovaMovimentacaoPage.css'

function NovaMovimentacaoPage() {
  const navigate = useNavigate()
  const [tiposMovimentacao, setTiposMovimentacao] = useState([])
  const [produtos, setProdutos] = useState([])
  const [produtosFiltrados, setProdutosFiltrados] = useState([])
  const [formData, setFormData] = useState({
    movementType: '',
    reason: ''
  })
  const [produtosSelecionados, setProdutosSelecionados] = useState([])
  const [produtoPesquisa, setProdutoPesquisa] = useState('')
  const [quantidadeAtual, setQuantidadeAtual] = useState('')
  const [produtoSelecionado, setProdutoSelecionado] = useState(null)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(true)

  useEffect(() => {
    loadOptions()
  }, [])

  useEffect(() => {
    if (produtoPesquisa) {
      const filtrados = produtos.filter(p => 
        p.name?.toLowerCase().includes(produtoPesquisa.toLowerCase()) ||
        p.sku?.toLowerCase().includes(produtoPesquisa.toLowerCase())
      )
      setProdutosFiltrados(filtrados.slice(0, 10))
    } else {
      setProdutosFiltrados([])
    }
  }, [produtoPesquisa, produtos])

  const loadOptions = async () => {
    setLoadingOptions(true)
    try {
      console.log('Carregando tipos de movimentação...')
      const tiposRes = await stockMovementTypeService.getAll()
      console.log('Resposta tipos:', tiposRes)
      
      // O serviço retorna diretamente o response.data que já tem { success, data }
      if (tiposRes && tiposRes.success) {
        // Filtrar apenas tipos relevantes (entry, exit, transfer)
        const tiposFiltrados = (tiposRes.data || []).filter(t => 
          ['entry', 'exit', 'transfer'].includes(t.type)
        )
        console.log('Tipos filtrados:', tiposFiltrados)
        setTiposMovimentacao(tiposFiltrados)
        
        if (tiposFiltrados.length === 0) {
          console.warn('Nenhum tipo de movimentação encontrado após filtro')
        }
      } else {
        console.warn('Resposta de tipos não tem success=true:', tiposRes)
        // Tentar usar diretamente se vier como array
        if (Array.isArray(tiposRes)) {
          const tiposFiltrados = tiposRes.filter(t => 
            ['entry', 'exit', 'transfer'].includes(t.type)
          )
          setTiposMovimentacao(tiposFiltrados)
        } else {
          console.error('Formato de resposta inesperado:', tiposRes)
          setErrors({ tipos: 'Erro ao carregar tipos de movimentação' })
        }
      }
      
      // Carregar produtos
      try {
        const produtosRes = await productService.getAll({ active: true })
        if (produtosRes && produtosRes.success) {
          setProdutos(produtosRes.data || [])
        } else if (Array.isArray(produtosRes)) {
          setProdutos(produtosRes)
        }
      } catch (prodError) {
        console.error('Erro ao carregar produtos:', prodError)
      }
    } catch (error) {
      console.error('Erro ao carregar opções:', error)
      console.error('Detalhes do erro:', error.response?.data || error.message)
      setErrors({ tipos: 'Erro ao carregar tipos de movimentação. Verifique o console.' })
    } finally {
      setLoadingOptions(false)
    }
  }

  const handleAddProduct = () => {
    if (!produtoSelecionado || !quantidadeAtual || parseInt(quantidadeAtual) <= 0) {
      setErrors({ produtos: 'Selecione um produto e informe uma quantidade válida' })
      return
    }

    // Verificar se produto já foi adicionado
    if (produtosSelecionados.find(p => p.id === produtoSelecionado.id)) {
      setErrors({ produtos: 'Este produto já foi adicionado à lista' })
      return
    }

    setProdutosSelecionados(prev => [
      ...prev,
      {
        id: produtoSelecionado.id,
        sku: produtoSelecionado.sku,
        name: produtoSelecionado.name,
        quantity: parseInt(quantidadeAtual)
      }
    ])

    setProdutoSelecionado(null)
    setQuantidadeAtual('')
    setProdutoPesquisa('')
    setErrors({})
  }

  const handleRemoveProduct = (index) => {
    setProdutosSelecionados(prev => prev.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    setErrors({})

    // Validações
    if (!formData.movementType) {
      setErrors({ movementType: 'Tipo de movimentação é obrigatório' })
      return
    }

    if (produtosSelecionados.length === 0) {
      setErrors({ produtos: 'Adicione pelo menos um produto à lista' })
      return
    }

    setLoading(true)

    try {
      // Criar uma movimentação para cada produto
      const promises = produtosSelecionados.map(produto => 
        stockMovementService.create({
          movementTypeId: parseInt(formData.movementType),
          productId: produto.id,
          quantity: produto.quantity,
          observation: formData.reason || null
        })
      )

      await Promise.all(promises)

      // Notificar movimentação de estoque para cada produto afetado
      produtosSelecionados.forEach(produto => {
        productCacheHelpers.notifyStockMovement(produto.id, {
          quantity: produto.quantity,
          movementType: formData.movementType
        });
      });

      // Redirecionar para a lista
      navigate('/erp/estoque/movimentacoes')
    } catch (error) {
      console.error('Erro ao salvar movimentações:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar movimentações'
      setErrors({ submit: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  const generateCode = () => {
    if (!formData.movementType) return 'Aguardando tipo...'
    const tipo = tiposMovimentacao.find(t => t.id === parseInt(formData.movementType))
    const prefix = tipo?.code || 'MOV'
    const timestamp = Date.now().toString().slice(-6)
    return `${prefix}-${timestamp}`
  }

  return (
    <div className="nova-movimentacao-page">
      <div className="page-header">
        <div>
          <h1>Nova Movimentação de Estoque</h1>
          <p>Criar uma nova movimentação de estoque</p>
        </div>
      </div>

      <div className="movement-form">
        {/* Primeiro Card: Informações Gerais */}
        <div className="form-card">
          <div className="card-title">
            <h2>Informações da Movimentação</h2>
          </div>
          <div className="card-content">
            <div className="form-row">
              <FormField
                label="Código"
                name="code"
                value={generateCode()}
                disabled
                placeholder="Gerado automaticamente"
              />

              <FormField
                label="Tipo de Movimentação"
                name="movementType"
                type="select"
                value={formData.movementType}
                onChange={(e) => setFormData({ ...formData, movementType: e.target.value })}
                error={errors.movementType || errors.tipos}
                required
                disabled={loadingOptions}
                options={[
                  { value: '', label: loadingOptions ? 'Carregando tipos...' : tiposMovimentacao.length === 0 ? 'Nenhum tipo disponível' : 'Selecione...' },
                  ...tiposMovimentacao.map(tipo => ({
                    value: tipo.id,
                    label: `${tipo.name} (${tipo.type === 'entry' ? 'Entrada' : tipo.type === 'exit' ? 'Saída' : 'Transferência'})`
                  }))
                ]}
              />
              
              {formData.movementType && (
                <div className={`movement-type-indicator ${tiposMovimentacao.find(t => t.id === parseInt(formData.movementType))?.type || ''}`}>
                  <span className="indicator-label">
                    Tipo: <strong>{tiposMovimentacao.find(t => t.id === parseInt(formData.movementType))?.type === 'entry' ? 'ENTRADA' : tiposMovimentacao.find(t => t.id === parseInt(formData.movementType))?.type === 'exit' ? 'SAÍDA' : 'TRANSFERÊNCIA'}</strong>
                  </span>
                </div>
              )}
            </div>

            <FormField
              label="Motivo do Movimento"
              name="reason"
              type="textarea"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              error={errors.reason}
              placeholder="Descreva o motivo da movimentação..."
              rows={3}
            />
          </div>
        </div>

        {/* Segundo Card: Produtos */}
        <div className="form-card">
          <div className="card-title">
            <h2>Produtos</h2>
          </div>
          <div className="card-content">
            <div className="add-product-section">
              <div className="product-search-wrapper">
                <div className="search-input-wrapper">
                  <Search size={18} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Pesquisar produto por nome ou SKU..."
                    value={produtoPesquisa}
                    onChange={(e) => setProdutoPesquisa(e.target.value)}
                    className="product-search-input"
                  />
                </div>
                
                {produtosFiltrados.length > 0 && (
                  <div className="product-dropdown">
                    {produtosFiltrados.map(produto => (
                      <div
                        key={produto.id}
                        className="product-option"
                        onClick={() => {
                          setProdutoSelecionado(produto)
                          setProdutoPesquisa(`${produto.sku} - ${produto.name}`)
                          setProdutosFiltrados([])
                        }}
                      >
                        <Package size={16} />
                        <div>
                          <div className="product-option-name">{produto.name}</div>
                          <div className="product-option-sku">SKU: {produto.sku} | Estoque: {produto.stock_quantity || 0}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {produtoPesquisa && produtosFiltrados.length === 0 && produtos.length > 0 && (
                  <div className="product-dropdown">
                    <div className="product-option no-results">
                      Nenhum produto encontrado
                    </div>
                  </div>
                )}
              </div>

              <div className="quantity-input-wrapper">
                <FormField
                  label="Quantidade"
                  name="quantity"
                  type="number"
                  value={quantidadeAtual}
                  onChange={(e) => setQuantidadeAtual(e.target.value)}
                  min="1"
                  placeholder="Qtd"
                />
              </div>

              <button
                className="btn-add-product"
                onClick={handleAddProduct}
                disabled={!produtoSelecionado || !quantidadeAtual}
              >
                <Plus size={20} />
                Adicionar
              </button>
            </div>

            {errors.produtos && (
              <div className="form-error-message">{errors.produtos}</div>
            )}

            {produtosSelecionados.length > 0 && (
              <div className="products-list">
                <h3>Produtos Adicionados ({produtosSelecionados.length})</h3>
                <div className="products-table">
                  <div className="table-header">
                    <div>Produto</div>
                    <div>SKU</div>
                    <div>Quantidade</div>
                    <div>Ações</div>
                  </div>
                  {produtosSelecionados.map((produto, index) => (
                    <div key={index} className="table-row">
                      <div>{produto.name}</div>
                      <div>{produto.sku}</div>
                      <div>{produto.quantity}</div>
                      <div>
                        <button
                          className="btn-remove"
                          onClick={() => handleRemoveProduct(index)}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {errors.submit && (
          <div className="form-error-message global-error">{errors.submit}</div>
        )}
      </div>

      {/* Botão Flutuante */}
      <FloatingActionButton
        icon={Save}
        onClick={handleSave}
        disabled={loading || produtosSelecionados.length === 0}
        label="Salvar Movimentação"
        variant="primary"
      />
    </div>
  )
}

export default NovaMovimentacaoPage

